// Floor sale, step 1: search or scan, pick a style.
import React, { useMemo, useState } from "react";
import { formatMoney } from "../../../../utils/fashion/money";
import { matchesStyle } from "./floorHelpers";
import { BagButton, FloorImage, Header } from "./FloorParts";

const PAGE = 60;

export default function FloorBrowse({ styles, categories, tillData, count, onBag, onOpenStyle, onScan, hasVariants }) {
  const [query, setQuery] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [limit, setLimit] = useState(PAGE);

  const chips = useMemo(() => {
    const used = new Set(styles.map((s) => s.categoryId).filter(Boolean));
    return (categories || [])
      .filter((c) => used.has(c.mProductCategoryId))
      .map((c) => ({ id: c.mProductCategoryId, name: c.name || c.value || "" }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [styles, categories]);

  const matching = useMemo(() => styles.filter((s) => (!categoryId || s.categoryId === categoryId) && matchesStyle(s, query)), [styles, categoryId, query]);

  // Enter (or Add) puts a tag's code on the bill through Core's scanner.
  const submit = (e) => {
    e.preventDefault();
    const code = query.trim();
    if (!code) return;
    onScan(code);
    setQuery("");
  };

  return (
    <>
      <Header>
        <b className="fp-fl-mk">Floor sale</b>
        <BagButton count={count} onClick={onBag} />
      </Header>
      <div className="fp-fl-body">
        <form className="fp-fl-srch" onSubmit={submit} role="search">
          <input
            className="fp-fl-field"
            aria-label="Search styles or enter a tag code"
            placeholder="Search style, or type a tag code"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setLimit(PAGE);
            }}
            autoComplete="off"
            enterKeyHint="go"
          />
          <button type="submit" className="fp-fl-btn fp-fl-pri" disabled={!query.trim()}>
            Add tag
          </button>
        </form>
        {chips.length > 1 && (
          <nav className="fp-fl-chips" aria-label="Categories">
            <button type="button" className={categoryId === "" ? "is-on" : ""} aria-pressed={categoryId === ""} onClick={() => setCategoryId("")}>
              All
            </button>
            {chips.map((c) => (
              <button key={c.id} type="button" className={categoryId === c.id ? "is-on" : ""} aria-pressed={categoryId === c.id} onClick={() => setCategoryId(c.id)}>
                {c.name}
              </button>
            ))}
          </nav>
        )}
        {!hasVariants && styles.length > 0 && <p className="fp-fl-hint">Sizes and colours aren't on this device yet. Run a Full Sync to load them.</p>}
        {matching.length === 0 ? (
          <div className="fp-fl-empty">
            <b>No styles found</b>
            <span>{query ? "Try a style name, or press Enter to add it as a tag code." : "Nothing in this category."}</span>
          </div>
        ) : (
          <div className="fp-fl-grid">
            {matching.slice(0, limit).map((s) => (
              <button key={s.styleId} type="button" className="fp-fl-tile" onClick={() => onOpenStyle(s)}>
                <FloorImage src={s.imageUrl} alt={s.name} className="fp-fl-ph" art={s.art} colour={s.colours[0]} />
                <span className="fp-fl-tn" title={s.name}>
                  {s.name}
                </span>
                <span className="fp-fl-tp fp-num">
                  {s.minPrice === s.maxPrice ? formatMoney(s.minPrice, tillData) : `${formatMoney(s.minPrice, tillData)}–${formatMoney(s.maxPrice, tillData)}`}
                </span>
              </button>
            ))}
          </div>
        )}
        {matching.length > limit && (
          <button type="button" className="fp-fl-btn" onClick={() => setLimit((n) => n + PAGE)}>
            Show more styles
          </button>
        )}
      </div>
    </>
  );
}
